'use client';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { PRODUCT_CONFIG } from '@eliadmin/core/src/constants/product-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';

export type ProductPriceProps = {
  product: Product;
  className?: {
    main?: string;
    discount?: string;
    discountWrapper?: string;
    price?: string;
    priceAfterDiscount?: string;
    priceBeforeDiscount?: string;
  };
};

export function ProductPrice({ product, className }: ProductPriceProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const currency = generalLayout.currency;
  const currencySettings = generalLayout.currency_settings;
  const { t } = useTranslations();

  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });

  const unit = templateSettings.product_detail.show_unit.enabled ? ` / ${product.unit}` : '';

  if (priceDisplay.priceOnRequest) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-between gap-1',
        'ea-product-price',
        className?.main,
      )}
    >
      {priceDisplay.showPreviousPrice ? (
        <div
          className={cn(
            'flex w-full flex-col items-center justify-between',
            'ea-product-price__discount-wrapper',
            className?.discountWrapper,
          )}
        >
          <Badge
            size="md"
            className={cn(
              'pointer-events-none flex flex-col items-center gap-1 rounded-md bg-destructive font-semibold text-destructive-foreground hover:bg-destructive',
              'ea-product-price__discount',
              className?.discount,
            )}
          >
            {React.cloneElement(PRODUCT_CONFIG.productPrice.discountIcon as any, {
              className: cn('ea-product-price__discount-icon'),
            })}
            <span>{`${t('text24')}`}</span>
            <span>{` -${priceDisplay.discountLabel}%`}</span>
          </Badge>
        </div>
      ) : null}
      <div className="flex flex-col items-center">
        {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
          <p className="whitespace-nowrap text-sm text-muted-foreground">
            {`${t('text1926')} ${formatPrice(
              priceDisplay.mocPrice,
              currency,
              currencySettings,
            )}${unit}`}
          </p>
        ) : null}
        <div
          className={cn(
            'flex items-center whitespace-nowrap',
            'ea-product-price__price',
            className?.price,
          )}
        >
          <div
            data-ea-visual-tag="h3"
            className={cn(
              'text-[32px] text-base font-bold leading-[37.5px]',
              'ea-product-price__price-after-discount',
              className?.priceAfterDiscount,
            )}
          >
            {formatPrice(priceDisplay.current, currency, currencySettings)} {unit}
          </div>
          {priceDisplay.showPreviousPrice ? (
            <del
              className={cn(
                'ml-2 text-[24px] text-sm font-normal leading-[28px] text-muted-foreground',
                'ea-product-price__price-before-discount',
                className?.priceBeforeDiscount,
              )}
            >{`${formatPrice(priceDisplay.beforeDiscount, currency, currencySettings)}${unit}`}</del>
          ) : null}
        </div>
        {templateSettings?.product_teaser?.price_without_vat?.enabled && (
          <p className="whitespace-nowrap text-sm text-muted-foreground">
            {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
            {formatPrice(priceDisplay.secondary, currency, currencySettings)} {unit}{' '}
            {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
          </p>
        )}
      </div>
    </div>
  );
}
